/** Fall-Logik der Seite /bezahlung (PaymentStatusPage), getrennt für Tests und Fast Refresh. */

import type { ApiError } from "@/lib/api";
import type { PaymentInfo } from "@/types/api";

/** Wie lange die Seite nach der Rückkehr aus Stripe auf die Bestätigung wartet. */
export const CONFIRM_POLL_MS = 2500;
export const CONFIRM_WAIT_MS = 60_000;

export type PaymentViewKind =
  | "loading"
  | "invalid"
  | "network_error"
  | "confirmed"
  | "waiting"
  | "waiting_long"
  | "open"
  | "aborted"
  | "stripe_error"
  | "deadline_passed"
  | "expired"
  | "cancelled"
  | "rejected"
  | "not_approved";

interface ResolveInput {
  token: string | null;
  ergebnis: string | null;
  info: PaymentInfo | undefined;
  error: ApiError | null;
  waitedTooLong: boolean;
}

/** Reine Fall-Logik, getestet in PaymentStatusPage.test.tsx. */
export function resolvePaymentView({
  token,
  ergebnis,
  info,
  error,
  waitedTooLong,
}: ResolveInput): PaymentViewKind {
  if (!token || ergebnis === "ungueltig") return "invalid";
  if (error) return error.status === 404 ? "invalid" : "network_error";
  if (!info) return "loading";

  switch (info.status) {
    case "confirmed":
      return "confirmed";
    case "expired":
      return "expired";
    case "cancelled":
      return "cancelled";
    case "rejected":
      return "rejected";
    case "draft":
    case "pending_admin":
      return "not_approved";
    case "awaiting_payment":
      // Zurück aus Stripe: Zahlung ist durch oder in Bearbeitung, Webhook steht aus.
      if (ergebnis === "danke")
        return waitedTooLong ? "waiting_long" : "waiting";
      if (!info.can_pay) return "deadline_passed";
      if (ergebnis === "fehler") return "stripe_error";
      if (ergebnis === "abgebrochen") return "aborted";
      return "open";
  }
}

function toGoogleDate(iso: string): string {
  return new Date(iso)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/** Termin vorausgefüllt in Google Kalender öffnen (kein Login bei uns nötig). */
export function googleCalendarUrl(info: PaymentInfo): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Raummiete ${info.room_name}`,
    dates: `${toGoogleDate(info.starts_at)}/${toGoogleDate(info.ends_at)}`,
    details: "Gebucht über die Tanzraum-Buchungsplattform.",
  });
  if (info.room_location) params.set("location", info.room_location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
